import { api } from '../../../api/client';

/**
 * Settings ▸ Analysis ▸ Daily QC Log Book Builder — the prototype detail page's API contract
 * (DailyQcBuilderController, /api/v1/daily-qc-logbook/builder). Validation, formula checks,
 * the recurrence summary and the preview evaluation are all server-side.
 */

export type FieldType = 'Date' | 'Time' | 'DateTime' | 'Numeric' | 'Text' | 'ComboBox' | 'Checkbox' | 'Attachment'
  | 'Employee' | 'Lookup' | 'AutoNumber';
export const FIELD_TYPES: FieldType[] = ['Date', 'Time', 'DateTime', 'Numeric', 'Text', 'ComboBox', 'Checkbox', 'Attachment',
  'Employee', 'Lookup', 'AutoNumber'];

/** Types whose Options cell is used, and what it holds (the API validates the same):
 *  ComboBox choices · Lookup source "TemplateID:Field" · AutoNumber prefix. */
export const OPTION_HINT: Partial<Record<FieldType, { placeholder: string; title: string }>> = {
  ComboBox: { placeholder: 'A, B, C', title: 'Choices, comma separated — use | instead when a choice itself has a comma (A, 1|B)' },
  Lookup: { placeholder: '1005:SampleID', title: 'Pick a value recorded in another logbook: TemplateID:Field' },
  AutoNumber: { placeholder: 'RW', title: 'Prefix of the generated number (prefix + yyMMdd + NN), letters and digits' },
};

/** ComboBox choices from the Options text — split on "|" when present, else on commas (as the API). */
export function splitOptions(s: string | null | undefined): string[] {
  if (!s?.trim()) return [];
  const sep = s.includes('|') ? '|' : ',';
  return [...new Set(s.split(sep).map((x) => x.trim()).filter(Boolean))];
}

/** One Selected field of the draft. `oid` null = added in this draft. */
export interface BuilderField {
  oid: string | null;
  name: string;
  caption: string | null;
  type: FieldType;
  options: string | null;
  default: string | null;
  width: number;
  freeze: boolean;
  symbol: string | null;
  calc: string | null;
  accept: string | null;
  /** False when the name is not a dbo.DailyQC column — its values are never saved. */
  storable: boolean;
  /** The name the Name column shows when it differs from the storage column `name`
   *  (e.g. "AutoclaveID" stored in UDCC1). Read-only; carried through Save unchanged. */
  displayName?: string | null;
}

/** The field name as the builder shows it (display name, else the storage column). */
export const shownName = (f: BuilderField) => (f.displayName?.trim() ? f.displayName.trim() : f.name);

export interface AvailableField { oid: string; name: string; type: FieldType; storable: boolean }

export type Frequency = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
export type Ends = 'never' | 'after' | 'by';

export interface Recurrence {
  frequency: Frequency;
  interval: number;
  weekDays: string[];
  pattern: 'day' | 'ordinal' | null;
  dayNumber: number;
  ordinal: string | null;
  month: number;
  ends: Ends;
  occurrences: number;
  endBy: string | null;   // yyyy-MM-dd
}

export interface BuilderTemplate {
  oid: string;
  templateId: number | null;
  templateName: string;
  categoryOid: string | null;
  testOid: string | null;
  methodOid: string | null;
  methodText: string | null;
  instrumentOid: string | null;
  instrumentCode: string | null;
  instrumentName: string | null;
  startDate: string | null;   // yyyy-MM-dd
  recurrence: Recurrence | null;
  recurrenceSummary: string;
  status: 'Active' | 'Retired';
  notifyOnFailure: boolean;
  instructions: string | null;
  fields: BuilderField[];
}

/** The draft the page edits (a template without the server-only bits). */
export type BuilderDraft = Omit<BuilderTemplate, 'oid' | 'templateId'>;

export interface Option { id: string; name: string }
export interface InstrumentOption { id: string; code: string; name: string | null }

export interface FormulaResult { valid: boolean; message: string; refs: string[] }

export interface PreviewResult {
  defaults: Record<string, string | null>;
  evaluation: {
    calc: Record<string, string | null>;
    accept: Record<string, boolean | null>;
    status: 'Pass' | 'Fail' | 'Incomplete';
    missing: string[];
  };
}

export const FORM_NAME = 'Daily QC Log Book Builder';
export const NO_RECURRENCE = 'No recurrence';
export const MAX_INSTRUCTIONS = 100;
export const MAX_TEMPLATE_NAME = 100;

const BASE = '/api/v1/daily-qc-logbook';
const B = `${BASE}/builder`;
const SILENT = { silentLoading: true } as never;
type Env<T> = { data: T };

export const emptyDraft = (): BuilderDraft => ({
  templateName: '', categoryOid: null, testOid: null, methodOid: null, methodText: null,
  instrumentOid: null, instrumentCode: null, instrumentName: null, startDate: null,
  recurrence: null, recurrenceSummary: NO_RECURRENCE, status: 'Active', notifyOnFailure: false,
  instructions: null, fields: [],
});

export async function loadTemplate(id: string): Promise<BuilderTemplate> {
  return ((await api.get(`${B}/${id}`)).data as Env<BuilderTemplate>).data;
}
export async function loadAvailable(): Promise<AvailableField[]> {
  return ((await api.get(`${B}/available`, SILENT)).data as Env<AvailableField[]>).data ?? [];
}
/** Instrument ID choices as G5 filters them: the instruments linked to the chosen QC Check +
 *  Method (none until both are set); `keepOid` (the current one) is always included. */
export async function loadInstruments(testOid: string | null, methodOid: string | null, keepOid: string | null): Promise<InstrumentOption[]> {
  const params = { testOid: testOid || undefined, methodOid: methodOid || undefined, keepOid: keepOid || undefined };
  return ((await api.get(`${B}/instruments`, { params, silentLoading: true } as never)).data as Env<InstrumentOption[]>).data ?? [];
}
export async function loadOptions(path: 'categories' | 'tests'): Promise<Option[]> {
  return ((await api.get(`${BASE}/${path}`, SILENT)).data as Env<Option[]>).data ?? [];
}
export async function loadMethods(testOid: string | null): Promise<Option[]> {
  if (!testOid) return [];
  return ((await api.get(`${BASE}/methods`, { params: { testOid }, silentLoading: true } as never)).data as Env<Option[]>).data ?? [];
}

/** Body of a save: the draft minus display-only values. */
function saveBody(d: BuilderDraft) {
  return {
    templateName: d.templateName, categoryOid: d.categoryOid, testOid: d.testOid, methodOid: d.methodOid,
    instrumentOid: d.instrumentOid, startDate: d.startDate || null, recurrence: d.recurrence, status: d.status,
    notifyOnFailure: d.notifyOnFailure, instructions: d.instructions, fields: d.fields,
  };
}
export async function createTemplate(d: BuilderDraft): Promise<{ oid: string; templateId: number }> {
  return ((await api.post(B, saveBody(d))).data as Env<{ oid: string; templateId: number }>).data;
}
export async function updateTemplate(id: string, d: BuilderDraft): Promise<void> {
  await api.put(`${B}/${id}`, saveBody(d));
}
export async function deleteTemplate(id: string): Promise<void> {
  await api.delete(`${BASE}/${id}`);
}

export async function checkFormula(fields: BuilderField[], index: number, kind: 'calc' | 'accept', text: string): Promise<FormulaResult> {
  return ((await api.post(`${B}/check-formula`, { fields, index, kind, text }, SILENT)).data as Env<FormulaResult>).data;
}
export async function describeRecurrence(recurrence: Recurrence | null, startDate: string | null): Promise<{ summary: string; errors: string[] }> {
  return ((await api.post(`${B}/recurrence`, { recurrence, startDate: startDate || null }, SILENT)).data as Env<{ summary: string; errors: string[] }>).data;
}
export async function previewRow(fields: BuilderField[], values: Record<string, string | null> | null): Promise<PreviewResult> {
  return ((await api.post(`${B}/preview`, { fields, values }, SILENT)).data as Env<PreviewResult>).data;
}

/** The server's message(s) — a VALIDATION error carries every problem in `details`. */
export function errorText(e: unknown, fallback: string): string {
  const err = (e as { response?: { data?: { error?: { message?: string; details?: unknown } } } })?.response?.data?.error;
  if (Array.isArray(err?.details) && err.details.length > 1) return (err.details as string[]).join('\n');
  return err?.message ?? fallback;
}

/** A field's caption for messages (falls back to its name). */
export const labelOf = (f: BuilderField) => (f.caption?.trim() ? f.caption.trim() : shownName(f));
